/** Analysis tab — new scoring engine layout (watchlist drawer). */

import React from "react";
import { SignalSystemBanner } from "./signalSystemUI.jsx";
import {
  buildDetailedScoreBreakdown,
  buildFibPriceStoryExtra,
  CONFLUENCE_HEADLINE,
  fmtTierScore,
} from "./scoreTierDisplay.js";
import { getRsiDisplayMeta, rsiDisplayColor } from "./rsiDisplay.js";

function CheckLine({ pass, label, detail, T }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "5px 0" }}>
      <span style={{ fontSize: 12, flexShrink: 0 }}>{pass ? "✅" : "❌"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: pass ? T.text : T.textDim }}>{label}</div>
        {detail && <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>{detail}</div>}
        {!pass && !detail && <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>Condition not met</div>}
      </div>
    </div>
  );
}

function CollapsibleModule({ title, defaultOpen = true, children, T }) {
  return (
    <details open={defaultOpen} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10 }}>
      <summary style={{ listStyle: "none", cursor: "pointer", padding: "12px 14px", fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em" }}>
        {title}
      </summary>
      <div style={{ padding: "0 14px 12px 14px", borderTop: `1px solid ${T.border}` }}>{children}</div>
    </details>
  );
}

function ScoreRow({ row, T }) {
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const ptColor = row.points > 0 ? T.green : row.points < 0 ? T.red : T.textDim;
  const icon = row.pass ? "✅" : row.points === 0 ? "—" : "❌";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 8, alignItems: "center", fontSize: 11, padding: "4px 0" }}>
      <span style={{ color: T.textSub }}>{row.label}</span>
      <span style={{ color: T.textDim, fontSize: 10, textAlign: "right" }}>{row.value}</span>
      <span style={{ width: 22, textAlign: "center" }}>{icon}</span>
      <span style={{ ...mono, fontWeight: 800, color: ptColor, minWidth: 32, textAlign: "right" }}>{fmtTierScore(row.points)}</span>
    </div>
  );
}

function AnalysisConfluenceSection({ result, T }) {
  const c = result?.confluence;
  const active = c?.active && n(c.bonus) > 0;
  const headline = c?.setupType ? (CONFLUENCE_HEADLINE[c.setupType] || c.label || "CONFLUENCE") : null;
  const reasons = Array.isArray(c?.reasons) && c.reasons.length
    ? c.reasons.join(" · ")
    : c?.reason || "";

  const enrichCondition = (row) => {
    const price = result.price;
    const fib500 = result.fibs?.fib500 ?? result.fibSwing?.fib500;
    const fib618 = result.fibs?.fib618 ?? result.fibSwing?.fib618;
    if (row.key === "nearFib505618" && fib500 != null) {
      return { ...row, label: `Price at Fib 50%/61.8% ($${fib500.toFixed(2)}${fib618 ? ` / $${fib618.toFixed(2)}` : ""})` };
    }
    if (row.key === "cmf" && result.cmf != null) {
      return { ...row, label: `Accumulation (CMF ${result.cmf.toFixed(2)})` };
    }
    if (row.key === "structure") {
      return { ...row, label: "Structure intact (Price > EMA50 > EMA200)" };
    }
    if (row.key === "volContract") {
      return { ...row, label: "Volume contracting on pullback" };
    }
    if (row.key === "obv" || row.key === "obvAccum") {
      return { ...row, label: row.pass ? "OBV rising" : "OBV flat or falling" };
    }
    return row;
  };

  if (!c?.conditions?.length) {
    return (
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 900, color: T.textDim, letterSpacing: "0.1em" }}>CONFLUENCE</div>
        <div style={{ marginTop: 10, fontSize: 12, color: T.textDim }}>No confluence setup active — rescan for tier-1 templates.</div>
      </div>
    );
  }

  if (active) {
    return (
      <div style={{ background: `${T.green}10`, border: `2px solid ${T.green}`, borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: "0.1em", marginBottom: 6 }}>CONFLUENCE</div>
        <div style={{ fontSize: 16, fontWeight: 900, color: T.text, letterSpacing: "0.02em", marginBottom: 6 }}>{headline}</div>
        {reasons && <div style={{ fontSize: 11, color: T.textSub, marginBottom: 12, lineHeight: 1.6 }}>{reasons}</div>}
        {c.conditions.map((row) => {
          const r = enrichCondition(row);
          return <CheckLine key={r.key || r.label} pass={r.pass === true} label={r.label} detail={r.detail} T={T} />;
        })}
      </div>
    );
  }

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 900, color: T.textDim, letterSpacing: "0.1em" }}>CONFLUENCE</div>
      <div style={{ marginTop: 8, fontSize: 12, color: T.textDim, marginBottom: 12 }}>
        No confluence setup active — closest template: {c.label || c.setupType} ({c.partialMatch ?? 0}/{c.conditions.length} met)
      </div>
      {c.conditions.map((row) => {
        const r = enrichCondition(row);
        return <CheckLine key={r.key || r.label} pass={r.pass === true} label={r.label} detail={!r.pass ? (r.detail || "Did not fire") : r.detail} T={T} />;
      })}
    </div>
  );
}

function n(v) {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function DetailedScoreSection({ result, T }) {
  const model = buildDetailedScoreBreakdown(result);
  if (!model) return null;
  const mono = { fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 12 }}>SCORE BREAKDOWN</div>
      {model.tiers.map((tier) => (
        <div key={tier.key} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textSub, letterSpacing: "0.06em", marginBottom: 6 }}>{tier.title}</div>
          {tier.rows.map((row, i) => (
            <ScoreRow key={`${tier.key}-${i}`} row={row} T={T} />
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, paddingTop: 6, borderTop: `1px dashed ${T.border}`, fontSize: 11 }}>
            <span style={{ color: T.textDim, fontWeight: 700 }}>Tier subtotal</span>
            <span style={{ ...mono, fontWeight: 900, color: tier.subtotal > 0 ? T.green : T.textDim }}>{fmtTierScore(tier.subtotal)}</span>
          </div>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
        <span style={{ fontWeight: 800, color: T.textSub }}>CONFLUENCE BONUS</span>
        <span style={{ ...mono, fontWeight: 900, color: model.confluenceBonus > 0 ? T.green : T.textDim }}>{fmtTierScore(model.confluenceBonus)}</span>
      </div>
      <div style={{ borderTop: `1px solid ${T.border}`, margin: "10px 0", paddingTop: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 6 }}>
          <span style={{ fontWeight: 800, color: T.text }}>RAW TOTAL</span>
          <span style={{ ...mono, fontWeight: 900, color: T.green }}>{fmtTierScore(model.rawTierTotal)}</span>
        </div>
        {model.tideDelta !== 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 6 }}>
            <span style={{ color: T.textSub }}>MARKET TIDE (×{model.tideMult.toFixed(1)})</span>
            <span style={{ ...mono, fontWeight: 800, color: model.tideDelta > 0 ? T.green : T.red }}>{fmtTierScore(model.tideDelta)}</span>
          </div>
        )}
        {model.macroAdj !== 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 6 }}>
            <span style={{ color: T.textSub }}>PHASE / MACRO ADJUSTMENT</span>
            <span style={{ ...mono, fontWeight: 800, color: model.macroAdj > 0 ? T.green : model.macroAdj < 0 ? T.red : T.textDim }}>{fmtTierScore(model.macroAdj)}</span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
          <span style={{ fontWeight: 900, color: T.text }}>FINAL (capped ±15)</span>
          <span style={{ ...mono, fontSize: 14, fontWeight: 900, color: model.finalScore >= 0 ? T.green : T.red }}>{fmtTierScore(model.finalScore)}</span>
        </div>
      </div>
    </div>
  );
}

function ModuleChecklistSection({ result, T }) {
  const model = buildDetailedScoreBreakdown(result);
  const price = result.price;
  const ema50 = model?.ema50 ?? result.sm50;
  const ema200 = model?.ema200 ?? result.sm200;
  const rsTrend = result.relativeStrength?.rs_line_trend;
  const cmf = result.cmf;
  const rsi = result.rsi;
  const rsiMeta = getRsiDisplayMeta(rsi);
  const rsiDiv = result.rsiDiv;
  const upDown = result.downVsUpVol;

  const fibZone = result.fibLevel
    ? `AT ${result.fibLevel} — ideal pullback entry`
    : "Between levels — watch 50% support";

  let upDownLabel = "—";
  if (upDown != null && Number.isFinite(upDown)) {
    upDownLabel = `${upDown.toFixed(1)} (${upDown > 1 ? "bullish" : upDown < 1 ? "bearish" : "neutral"})`;
  }

  const rsiNote = rsi != null && rsi > 70
    ? "Note: RSI > 70 is strong momentum — not a concern for this engine."
    : rsi != null && rsi > 50
    ? "Note: RSI holds above midline — momentum supportive."
    : null;

  const crossDays = model?.rsiCrossDays;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <CollapsibleModule title="MODULE 1 — TREND STRUCTURE" T={T}>
        <CheckLine pass={price != null && ema50 != null && price > ema50} label={`Price > EMA50${ema50 != null ? ` ($${ema50.toFixed(2)})` : ""}`} T={T} />
        <CheckLine pass={ema50 != null && ema200 != null && ema50 > ema200} label="EMA50 > EMA200 (golden structure)" T={T} />
        <CheckLine pass={rsTrend === "RISING"} label="RS line rising 20d" T={T} />
        <CheckLine pass={!!model?.don?.aboveMid} label="Donchian: above midline" T={T} />
        <CheckLine pass={!!model?.don?.breaking} label="Donchian breakout" detail={model?.don?.breaking ? "Confirmed" : "Not yet"} T={T} />
        <div style={{ fontSize: 10, color: T.yellow, marginTop: 8, fontWeight: 700 }}>Fib zone: {fibZone}</div>
      </CollapsibleModule>

      <CollapsibleModule title="MODULE 2 — VOLUME & ACCUMULATION" T={T}>
        <CheckLine pass={cmf != null && cmf > 0.05} label={`CMF: ${cmf != null ? cmf.toFixed(2) : "—"}${cmf != null && cmf > 0.05 ? " (accumulation)" : ""}`} T={T} />
        <CheckLine pass={!!result.obv?.rising} label={`OBV: ${result.obv?.rising ? "rising" : "flat/falling"}`} T={T} />
        <CheckLine pass={!!model?.volContracting} label="Volume contracting on pullback" T={T} />
        <CheckLine pass={!!result.volBreakout?.volConfirmed} label="Breakout volume" detail={result.volBreakout?.volConfirmed ? "Confirmed" : "Not yet"} T={T} />
        <div style={{ fontSize: 10, color: T.textSub, marginTop: 8 }}>Up/Down volume ratio: {upDownLabel}</div>
      </CollapsibleModule>

      <CollapsibleModule title="MODULE 3 — MOMENTUM & DIVERGENCE" T={T}>
        <div style={{ fontSize: 12, fontWeight: 700, color: rsi != null ? rsiDisplayColor(rsi, T) : T.textDim, marginBottom: 8 }}>
          RSI: {rsi != null ? rsi.toFixed(0) : "—"} — {rsiMeta.label}
        </div>
        <div style={{ fontSize: 11, color: T.textSub, marginBottom: 8 }}>
          RSI divergence: {rsiDiv?.bullish ? "bullish detected" : rsiDiv?.bearish ? "bearish detected" : "none active"}
        </div>
        {result?.rsiDivergenceContext && (
          <div style={{ fontSize: 10, color: T.textDim, lineHeight: 1.55, marginBottom: 10, padding: "8px 10px", background: `${T.border}18`, borderRadius: 8, border: `1px solid ${T.border}` }}>
            {result.rsiDivergenceContext}
          </div>
        )}
        <CheckLine pass={rsi != null && rsi >= 50} label="RSI above midline (50)" T={T} />
        <CheckLine
          pass={crossDays != null}
          label={crossDays != null ? `RSI cross above 50: ${crossDays === 0 ? "today" : `${crossDays} day(s) ago`}` : "RSI cross above 50"}
          T={T}
        />
        {rsiNote && <div style={{ fontSize: 10, color: T.textDim, marginTop: 8, lineHeight: 1.5 }}>{rsiNote}</div>}
      </CollapsibleModule>
    </div>
  );
}

function AnalysisPriceStorySection({ result, T }) {
  const story = result?.priceStoryParagraph;
  const fibExtra = buildFibPriceStoryExtra(result);
  if (!story && !fibExtra) return null;
  return (
    <div style={{ padding: "14px 16px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>📖 PRICE STORY</div>
      {story && <div style={{ fontSize: 12, color: T.text, lineHeight: 1.85, whiteSpace: "pre-wrap" }}>{story}</div>}
      {fibExtra && (
        <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.75, marginTop: story ? 12 : 0, paddingTop: story ? 12 : 0, borderTop: story ? `1px solid ${T.border}` : "none" }}>
          {fibExtra}
        </div>
      )}
    </div>
  );
}

function PostMoveHighBanner({ result, T }) {
  const a = result?.postMoveAssessment;
  if (!a || a.riskLevel !== "HIGH") return null;
  return (
    <div style={{ padding: "12px 14px", background: "#f9731618", border: `2px solid #f97316`, borderRadius: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 900, color: "#f97316", letterSpacing: "0.08em", marginBottom: 6 }}>⚠ POST-MOVE — HIGH RISK</div>
      <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.6 }}>{a.guidanceText || a.context?.join(" · ") || "Extended move — do not chase new entries."}</div>
    </div>
  );
}

export function SignalTimelinePanel({ result, T }) {
  const ew = result?.earlyWarning;
  if (!ew) return null;
  const pSteps = [ew?.phase1Any, ew?.phase2Any, !!(result?.callsSignal?.fullSignal || result?.callsSignal?.bouncePlayOnly || result?.putsSignal?.fullSignal)].filter(Boolean).length;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>⏱ SIGNAL TIMELINE</div>
      <div style={{ height: 8, borderRadius: 4, background: T.border, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ width: `${Math.round((pSteps / 3) * 100)}%`, height: "100%", background: `linear-gradient(90deg, ${T.cyan}, ${T.purple})` }} />
      </div>
      <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.75 }}>
        {(ew.timelineLines || []).map((line, i) => (
          <div key={i}>{line}</div>
        ))}
        {result?.cmf != null && (
          <div>Phase 3: {result.cmf > 0.05 ? "✅ CMF confirms institutional buying" : "⏳ Waiting for CMF confirmation (>0.05)"}</div>
        )}
        {result?.phaseProgressionBonus > 0 && ew?.progressionNote && (
          <div style={{ marginTop: 8, fontWeight: 700, color: T.green }}>{ew.progressionNote}</div>
        )}
      </div>
    </div>
  );
}

export function AnalysisTabPanel({ result, T, inPosition = false, activeSignal = null }) {
  if (!result) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <PostMoveHighBanner result={result} T={T} />
      <AnalysisConfluenceSection result={result} T={T} />
      <DetailedScoreSection result={result} T={T} />
      <SignalSystemBanner result={result} T={T} inPosition={inPosition} activeSignal={activeSignal} />
      <ModuleChecklistSection result={result} T={T} />
      <AnalysisPriceStorySection result={result} T={T} />
    </div>
  );
}
